import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { costosAPI, animalesAPI } from '../services/api';
import { toast } from 'sonner';

const CATEGORIAS = [
  { id: 'alimentacion', label: 'Alimentación' },
  { id: 'sanidad', label: 'Sanidad' },
  { id: 'personal', label: 'Personal' },
  { id: 'insumos', label: 'Insumos' },
  { id: 'transporte', label: 'Transporte' },
  { id: 'infraestructura', label: 'Infraestructura' },
  { id: 'otros', label: 'Otros' },
];
const catLabel = (id) => CATEGORIAS.find(c => c.id === id)?.label || id;
const fmt = (n) => (n == null ? '—' : Number(n).toLocaleString('es-PY', { maximumFractionDigits: 2 }));

const Kpi = ({ label, value, border, sub, color = 'text-gray-900', testid }) => (
  <div className={`bg-white rounded-xl shadow-sm p-4 border-l-4 ${border}`} data-testid={testid}>
    <p className="text-xs font-medium text-gray-500 uppercase">{label}</p>
    <p className={`text-2xl font-bold mt-1 ${color}`}>{value}</p>
    {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
  </div>
);

export default function CostosPage() {
  const { user } = useAuth();
  const [margen, setMargen] = useState(null);
  const [costos, setCostos] = useState([]);
  const [ventas, setVentas] = useState([]);
  const [animales, setAnimales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCosto, setShowCosto] = useState(false);
  const [showVenta, setShowVenta] = useState(false);
  const hoy = new Date().toISOString().slice(0, 10);
  const [fCosto, setFCosto] = useState({ categoria: 'alimentacion', concepto: '', monto: '', fecha: hoy });
  const [fVenta, setFVenta] = useState({ animal_ids: [], fecha: hoy, kg_total: '', precio_total: '', comprador: '' });

  const loadData = useCallback(async () => {
    if (!user?.establecimiento_id) return;
    setLoading(true);
    try {
      const [m, c, v, a] = await Promise.all([
        costosAPI.margen({ establecimiento_id: user.establecimiento_id }),
        costosAPI.listar({ establecimiento_id: user.establecimiento_id }),
        costosAPI.listarVentas(user.establecimiento_id),
        animalesAPI.getAll({ establecimiento_id: user.establecimiento_id, page_size: 500, estado: 'activo' }),
      ]);
      setMargen(m.data);
      setCostos(c.data.costos);
      setVentas(v.data.ventas);
      setAnimales(a.data.data || []);
    } catch { toast.error('Error cargando datos'); }
    setLoading(false);
  }, [user]);

  useEffect(() => { loadData(); }, [loadData]);

  const submitCosto = async (e) => {
    e.preventDefault();
    try {
      await costosAPI.registrar({ ...fCosto, monto: parseFloat(fCosto.monto), establecimiento_id: user.establecimiento_id });
      toast.success('Costo registrado');
      setShowCosto(false);
      setFCosto({ categoria: 'alimentacion', concepto: '', monto: '', fecha: hoy });
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al registrar costo'); }
  };

  const submitVenta = async (e) => {
    e.preventDefault();
    if (!fVenta.animal_ids.length) { toast.error('Seleccioná al menos un animal'); return; }
    try {
      await costosAPI.registrarVenta({
        ...fVenta,
        precio_total: parseFloat(fVenta.precio_total),
        kg_total: fVenta.kg_total ? parseFloat(fVenta.kg_total) : null,
        establecimiento_id: user.establecimiento_id,
      });
      toast.success('Venta registrada');
      setShowVenta(false);
      setFVenta({ animal_ids: [], fecha: hoy, kg_total: '', precio_total: '', comprador: '' });
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al registrar venta'); }
  };

  const delCosto = async (id) => { try { await costosAPI.eliminar(id); toast.success('Costo eliminado'); loadData(); } catch { toast.error('Error'); } };
  const delVenta = async (id) => { try { await costosAPI.eliminarVenta(id); toast.success('Venta eliminada, animales reactivados'); loadData(); } catch { toast.error('Error'); } };

  if (loading) return (
    <div className="p-6 flex items-center justify-center min-h-[60vh]">
      <div className="inline-block w-8 h-8 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  const m = margen || {};
  const margenPositivo = (m.margen || 0) >= 0;

  return (
    <div className="p-4 sm:p-6 space-y-6" data-testid="costos-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Costos & Margen</h2>
          <p className="text-sm text-gray-500 mt-1">Cuánto gastás, cuánto ingresás y cuánto te queda</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setShowCosto(!showCosto)} className="px-4 py-2 bg-red-600 text-white text-sm font-semibold rounded-lg hover:bg-red-700 transition" data-testid="btn-nuevo-costo">+ Costo</button>
          <button onClick={() => setShowVenta(!showVenta)} className="px-4 py-2 bg-emerald-600 text-white text-sm font-semibold rounded-lg hover:bg-emerald-700 transition" data-testid="btn-nueva-venta">+ Venta</button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Kpi label="Ingresos (ventas)" value={fmt(m.total_ingresos)} border="border-emerald-500" sub={`${m.animales_vendidos || 0} animales vendidos`} color="text-emerald-700" testid="kpi-ingresos" />
        <Kpi label="Costos totales" value={fmt(m.total_costos)} border="border-red-400" sub={`${costos.length} registros`} color="text-red-700" testid="kpi-costos" />
        <Kpi label="Margen" value={fmt(m.margen)} border={margenPositivo ? 'border-emerald-600' : 'border-red-600'} color={margenPositivo ? 'text-emerald-700' : 'text-red-700'} sub={margenPositivo ? 'Ganancia' : 'Pérdida'} testid="kpi-margen" />
        <Kpi label="Costo / cabeza" value={fmt(m.costo_por_cabeza)} border="border-amber-400" sub={`${m.animales_activos || 0} activos`} testid="kpi-costo-cabeza" />
      </div>

      {m.precio_promedio_kg && (
        <p className="text-sm text-gray-500">Precio promedio de venta: <strong>{fmt(m.precio_promedio_kg)}/kg</strong> ({fmt(m.kg_vendidos)} kg vendidos)</p>
      )}

      {showCosto && (
        <form onSubmit={submitCosto} className="bg-white rounded-xl shadow-sm p-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3" data-testid="form-costo">
          <select value={fCosto.categoria} onChange={e => setFCosto({ ...fCosto, categoria: e.target.value })} className="border rounded-lg px-3 py-2 text-sm" data-testid="costo-categoria">
            {CATEGORIAS.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
          </select>
          <input required placeholder="Concepto (ej: Sal mineral)" value={fCosto.concepto} onChange={e => setFCosto({ ...fCosto, concepto: e.target.value })} className="border rounded-lg px-3 py-2 text-sm" data-testid="costo-concepto" />
          <input required type="number" step="0.01" min="0.01" placeholder="Monto" value={fCosto.monto} onChange={e => setFCosto({ ...fCosto, monto: e.target.value })} className="border rounded-lg px-3 py-2 text-sm" data-testid="costo-monto" />
          <input required type="date" value={fCosto.fecha} onChange={e => setFCosto({ ...fCosto, fecha: e.target.value })} className="border rounded-lg px-3 py-2 text-sm" data-testid="costo-fecha" />
          <button type="submit" className="px-4 py-2 bg-gray-900 text-white text-sm font-semibold rounded-lg hover:bg-gray-800" data-testid="costo-guardar">Guardar costo</button>
        </form>
      )}

      {showVenta && (
        <form onSubmit={submitVenta} className="bg-white rounded-xl shadow-sm p-5 space-y-3" data-testid="form-venta">
          <p className="text-sm font-semibold text-gray-700">Seleccioná los animales vendidos ({fVenta.animal_ids.length} elegidos):</p>
          <div className="max-h-40 overflow-y-auto border rounded-lg p-2 grid grid-cols-2 sm:grid-cols-4 gap-1">
            {animales.map(a => (
              <label key={a.id} className="flex items-center gap-2 text-sm px-2 py-1 hover:bg-gray-50 rounded cursor-pointer">
                <input type="checkbox" checked={fVenta.animal_ids.includes(a.id)}
                  onChange={e => setFVenta({ ...fVenta, animal_ids: e.target.checked ? [...fVenta.animal_ids, a.id] : fVenta.animal_ids.filter(x => x !== a.id) })} />
                <span>{a.VID} <span className="text-gray-400">({a.categoria || a.sexo})</span></span>
              </label>
            ))}
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
            <input required type="date" value={fVenta.fecha} onChange={e => setFVenta({ ...fVenta, fecha: e.target.value })} className="border rounded-lg px-3 py-2 text-sm" data-testid="venta-fecha" />
            <input type="number" step="0.1" min="0" placeholder="Kg totales (opcional)" value={fVenta.kg_total} onChange={e => setFVenta({ ...fVenta, kg_total: e.target.value })} className="border rounded-lg px-3 py-2 text-sm" data-testid="venta-kg" />
            <input required type="number" step="0.01" min="0.01" placeholder="Precio total" value={fVenta.precio_total} onChange={e => setFVenta({ ...fVenta, precio_total: e.target.value })} className="border rounded-lg px-3 py-2 text-sm" data-testid="venta-precio" />
            <input placeholder="Comprador (opcional)" value={fVenta.comprador} onChange={e => setFVenta({ ...fVenta, comprador: e.target.value })} className="border rounded-lg px-3 py-2 text-sm" data-testid="venta-comprador" />
            <button type="submit" className="px-4 py-2 bg-emerald-700 text-white text-sm font-semibold rounded-lg hover:bg-emerald-800" data-testid="venta-guardar">Registrar venta</button>
          </div>
        </form>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow-sm p-5">
          <h3 className="font-bold text-gray-900 mb-3">Costos por categoría</h3>
          {(m.costos_por_categoria || []).length === 0 ? (
            <p className="text-sm text-gray-400" data-testid="costos-empty">Registrá tu primer costo con el botón <strong>+ Costo</strong> para ver el desglose acá.</p>
          ) : (
            <div className="space-y-2">
              {m.costos_por_categoria.map(c => (
                <div key={c.categoria} className="flex items-center justify-between text-sm">
                  <span className="text-gray-700">{catLabel(c.categoria)} <span className="text-gray-400">({c.cantidad})</span></span>
                  <div className="flex items-center gap-3 flex-1 mx-3">
                    <div className="flex-1 bg-gray-100 rounded-full h-2">
                      <div className="bg-red-400 h-2 rounded-full" style={{ width: `${m.total_costos ? (c.total / m.total_costos) * 100 : 0}%` }} />
                    </div>
                  </div>
                  <span className="font-semibold text-gray-900">{fmt(c.total)}</span>
                </div>
              ))}
            </div>
          )}
          <div className="mt-4 max-h-56 overflow-y-auto divide-y">
            {costos.slice(0, 30).map(c => (
              <div key={c.id} className="flex items-center justify-between py-2 text-sm">
                <div><span className="font-medium">{c.concepto}</span> <span className="text-xs text-gray-400">{catLabel(c.categoria)} · {c.fecha}</span></div>
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-red-700">{fmt(c.monto)}</span>
                  <button onClick={() => delCosto(c.id)} className="text-gray-300 hover:text-red-500" title="Eliminar" data-testid={`del-costo-${c.id}`}>✕</button>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-5">
          <h3 className="font-bold text-gray-900 mb-3">Ventas</h3>
          {ventas.length === 0 ? (
            <p className="text-sm text-gray-400" data-testid="ventas-empty">Registrá tu primera venta con el botón <strong>+ Venta</strong>. Los animales vendidos pasan automáticamente a estado "vendido".</p>
          ) : (
            <div className="max-h-80 overflow-y-auto divide-y">
              {ventas.map(v => (
                <div key={v.id} className="flex items-center justify-between py-2 text-sm">
                  <div>
                    <span className="font-medium">{(v.vids || []).slice(0, 4).join(', ')}{(v.vids || []).length > 4 ? ` +${v.vids.length - 4}` : ''}</span>
                    <p className="text-xs text-gray-400">{v.fecha}{v.kg_total ? ` · ${fmt(v.kg_total)} kg` : ''}{v.comprador ? ` · ${v.comprador}` : ''}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-emerald-700">{fmt(v.precio_total)}</span>
                    <button onClick={() => delVenta(v.id)} className="text-gray-300 hover:text-red-500" title="Eliminar" data-testid={`del-venta-${v.id}`}>✕</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
